import React, { useEffect, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';
import styled from 'styled-components';

import { Avatar, Paper, Tooltip } from '@mui/material';

import { EventParticipant } from './types/eventTypes';
import { IRoomSessionParticipant, IRoomState, RoomAgendaEvent } from './types/roomAgenda';
import Text from 'components/Text';
import { apiMeeting } from 'services/apiService';

const Container = styled.div``;

const Panel = styled.div`
  background: ${({ theme }) => theme.COLORS.BACKGROUND_SECONDARY};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OnlineCount = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: #16c784; /* success green */
`;

const GreenDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #16c784;
  box-shadow: 0 0 0 2px rgba(22, 199, 132, 0.25);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
  gap: 4px;
`;

interface Props {
  event: RoomAgendaEvent;
  participants: EventParticipant[];
  roomState: IRoomState | null;
}

const RoomEventParticipants: React.FC<Props> = ({ event, participants, roomState }) => {
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  useEffect(() => {
    if (!roomState?.is_open) return;

    let intervalId: any;
    let attempts = 0;

    const fetchOnlineUsers = async () => {
      const response: AxiosResponse<IRoomSessionParticipant[]> = await apiMeeting.get(
        `/v1/participants/${roomState.room_id}/${roomState.session_id}?online=true`
      );
      setOnlineUsers(response.data.map((p: IRoomSessionParticipant) => p.user_id));
    };

    const tick = async () => {
      attempts += 1;
      await fetchOnlineUsers();
      if (attempts >= 30) {
        clearInterval(intervalId);
      }
    };

    // initial immediate fetch counts as the first attempt
    tick();
    intervalId = setInterval(tick, 5000);

    return () => clearInterval(intervalId);
  }, [roomState?.room_id, roomState?.session_id, roomState?.is_open]);

  const allParticipants = participants || [];

  // Heuristic: separate members from staff roles
  const staffCodes = ['PRESENTER', 'COORDINATOR', 'CURATOR'];
  const members = useMemo(
    () =>
      allParticipants.filter((p: EventParticipant) => {
        const code = p?.role;
        return !staffCodes.includes(code || '');
      }),
    [allParticipants]
  );

  const onlineSet = useMemo(() => new Set(onlineUsers), [onlineUsers]);

  const sortedMembers = useMemo(() => {
    const copy = [...members];
    return copy.sort(
      (a, b) => Number(onlineSet.has(b.user_uuid)) - Number(onlineSet.has(a.user_uuid))
    );
  }, [members, onlineSet]);

  const getAvatar = (p: EventParticipant) => p?.photo_url || undefined;

  const onlineCount = onlineUsers.length || 0;

  const totalCount = members.length || allParticipants.length || 0;

  return (
    <Paper sx={{ width: '100%', height: '100%', minWidth: 220 }}>
      <HeaderRow>
        <Text size="medium" bold>
          Membros ({totalCount})
        </Text>
        {onlineCount > 0 && roomState?.is_open ? (
          <OnlineCount>
            <GreenDot />
            <span>{onlineCount} Online</span>
          </OnlineCount>
        ) : null}
      </HeaderRow>

      <div style={{ marginTop: 12 }}>
        <Grid>
          {sortedMembers.map((p: EventParticipant, idx: number) => {
            const isOnline = onlineSet.has(p.user_uuid);
            return (
              <Tooltip key={p.user_uuid || idx} title={p.first_name + ' ' + p.last_name}>
                <Avatar
                  src={getAvatar(p)}
                  alt={`${p.first_name} ${p.last_name}`}
                  sx={{
                    width: 36,
                    height: 36,
                    filter: isOnline ? 'none' : 'grayscale(100%) brightness(0.7)',
                    opacity: isOnline ? 1 : 0.55,
                  }}
                />
              </Tooltip>
            );
          })}
        </Grid>
      </div>
    </Paper>
  );
};

export default RoomEventParticipants;
